import { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "vega-icons";

import { SecondaryButton } from "@vega-core/components/Buttons/SecondaryButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { PDFViewer } from "@vega-core/components/PDFViewer/PDFViewer";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { WarningModal } from "../WarningModal";

import { TermsAndConditionsEmptyState } from "./TermsAndConditionsEmptyState";

export type TermsAndConditionsViewerDocument = {
  id: string;
  label: string;
  signedUrl: string | undefined;
};

type Props = {
  documents: TermsAndConditionsViewerDocument[];
  onAccept: VoidFunction;
  isAccepting: boolean;
};

export const TermsAndConditionsViewer = ({
  documents,
  onAccept,
  isAccepting,
}: Props) => {
  const {
    value: isWarningModalOpen,
    setTrue: openWarningModal,
    setFalse: closeWarningModal,
  } = useBoolean();
  const [currentIndex, setCurrentIndex] = useState(0);

  const isMobile = !useDeviceWidth("sm");
  const isTablet = useDeviceWidth("md");

  const dropdownOptions = documents.map((document, index) => ({
    label: document.label || m.welcome_terms_heading(),
    value: String(index),
  }));
  const indexMap = Object.fromEntries(
    dropdownOptions.map((option, index) => [option.value, index]),
  );

  const currentDocument = documents[currentIndex];
  const contentRef = useRef<HTMLDivElement>(null);

  const handleDropdownChange = (option: { value: string } | undefined) => {
    if (option?.value !== undefined && option.value in indexMap) {
      setCurrentIndex(indexMap[option.value]);
    }
  };

  const handlePrevious = () => setCurrentIndex((prev) => prev - 1);

  const handleNext = () => setCurrentIndex((prev) => prev + 1);

  const isFirstDocument = currentIndex === 0;
  const isLastDocument = currentIndex === documents.length - 1;

  useEffect(
    function scrollDocumentToTop() {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    },
    [currentIndex],
  );

  if (documents.length === 0) {
    return <TermsAndConditionsEmptyState />;
  }

  return (
    <>
      <div
        className={cnTwMerge(
          "w-full lg:max-w-4xl 2xl:max-w-5xl p-6 flex flex-col gap-4 overflow-hidden touch-none",
          isMobile && "h-full",
        )}
      >
        <h4
          className={cnTwMerge("heading-4 text-center", isMobile && "shrink-0")}
        >
          {m.welcome_terms_heading()}
        </h4>
        <div
          className={cnTwMerge(
            "flex flex-col lg:flex-row items-center justify-between w-full gap-4",
            isMobile && "shrink-0",
          )}
        >
          <ThemedDropdown
            fullWidth
            isClearable={false}
            name="terms-and-conditions"
            onChange={handleDropdownChange}
            options={dropdownOptions}
            value={dropdownOptions[currentIndex]?.value}
          />
          <div className="flex  items-center justify-between w-full min-w-40 gap-2">
            <ThemedIconButton
              ariaLabel={m.btn_back()}
              dataTestId="previous-document-button"
              disabled={isFirstDocument}
              icon={<ArrowLeftIcon />}
              onClick={handlePrevious}
              size="medium"
            />
            <div className="flex items-center heading-desktop-h6 gap-2">
              <div>{currentIndex + 1}</div>
              <div className="lowercase">{m.common_of()}</div>
              <div>{documents.length}</div>
            </div>
            <ThemedIconButton
              ariaLabel={m.btn_next()}
              dataTestId="next-document-button"
              disabled={isLastDocument}
              icon={<ArrowRightIcon />}
              onClick={handleNext}
              size="medium"
            />
          </div>
        </div>
        <div
          className={cnTwMerge(isMobile && "flex-1 min-h-0")}
          ref={contentRef}
        >
          <PDFViewer
            blobOrString={currentDocument.signedUrl}
            embedded
            initialScale={isTablet ? 1 : 0.75}
            overflowBehavior="auto"
            previewDocumentId={currentDocument.id}
          />
        </div>

        <div className={cnTwMerge("flex w-full gap-4", isMobile && "shrink-0")}>
          <SecondaryButton
            className="w-full"
            dataTestId="decline-button"
            onClick={openWarningModal}
          >
            {m.btn_decline()}
          </SecondaryButton>
          <ThemedButton
            className="w-full"
            dataTestId="accept-button"
            disabled={isAccepting}
            fullWidth
            isLoading={isAccepting}
            onClick={onAccept}
            variant="primary"
          >
            {m.btn_acceptAll()}
          </ThemedButton>
        </div>
      </div>
      <WarningModal onClose={closeWarningModal} open={isWarningModalOpen} />
    </>
  );
};
